import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCopyToClipboard } from 'hooks/useCopyToClipboard';
import { logError } from 'services/logging/logging';
import { useCurrentUser } from 'services/repositories/user/user';
import { getSessionId, track } from 'support/helpers/analytics/analytics';
import { DocuparseResponseSchema, extractDataFromSpec, useUnattendedOcrEmail } from 'support/helpers/ocr/ocr';
import { PartnerDTO } from 'support/types';
import { DocumentType, DocumentTypeWithVirtual, ProcessCanonicalField } from 'support/types/dtos';
import { AlertSection } from 'components/Display/Alert';
import { Alert } from 'components/Display/Alert/Alert';
import { Button } from 'components/Form/Button/Button';
import { FileInput, FileInputErrorCodes, isFileInputValidationError } from 'components/Form/FileInput/FileInput';
import { Translate } from 'components/Translate/Translate';

import { useProcessingLabel } from './hooks/useProcessingLabel';
import { ocrUploadFileLoader } from './helpers';

type OCRUploadProps = {
  relationshipId: string | undefined;
  sourceDocumentId?: string;
  documentType: DocumentType;
  virtualDocumentType: DocumentTypeWithVirtual;
  currentPartner: PartnerDTO | undefined;
  onExtracted: (data: any, documentHash: string) => void;
  senderPartner: Pick<PartnerDTO, 'id' | 'name'>;
  receiverPartner: Pick<PartnerDTO, 'id' | 'name'>;
  senderSpec: { fields: Array<ProcessCanonicalField> };
  receiverSpec: { fields: Array<ProcessCanonicalField> };
  initialData?: any;
  trackingIds: {
    uploadStarted: string;
    uploadSuccessful: string;
  };
};

type ExtractionError = {
  title: string;
  message: string;
};
export const OCRUpload = ({
  relationshipId,
  sourceDocumentId,
  initialData,
  documentType,
  virtualDocumentType,
  currentPartner,
  onExtracted,
  senderPartner,
  receiverPartner,
  senderSpec,
  receiverSpec,
  trackingIds,
}: OCRUploadProps) => {
  const { data: currentUser } = useCurrentUser();
  const { t } = useTranslation();
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState<ExtractionError>();
  const processingLabel = useProcessingLabel({ enabled: isExtracting });
  const unattendedOcrEmail = useUnattendedOcrEmail({ relationshipId, currentPartner });
  const loader = useCallback(
    (file: File) => {
      track(trackingIds.uploadStarted);
      return ocrUploadFileLoader({ currentPartnerId: currentPartner?.id, documentType, sourceDocumentId })(file);
    },
    [currentPartner?.id, documentType, sourceDocumentId, trackingIds.uploadStarted],
  );

  const onError = useCallback(
    (error: any) => {
      setExtractionError({
        title: t('common:ocr.upload.error.title'),
        message: error.message,
      });
      if (!isFileInputValidationError(error)) {
        logError(error);
      }
    },
    [t],
  );

  const onLoadFile = async (data: DocuparseResponseSchema['/v1/upload']['data']) => {
    setIsExtracting(true);
    setExtractionError(undefined);
    track(trackingIds.uploadSuccessful);

    extractDataFromSpec({
      hash: data.hash,
      documentType,
      virtualDocumentType,
      initialData,
      senderSpec,
      receiverSpec,
      isAdmin: Boolean(currentUser?.isAdmin),
      sessionId: getSessionId(),
      senderPartner: {
        id: senderPartner.id,
        name: senderPartner.name,
      },
      receiverPartner: {
        id: receiverPartner.id,
        name: receiverPartner.name,
      },
    })
      .then((content) => {
        onExtracted(content, data.hash);
      })
      .catch((e: Error) => {
        setExtractionError({
          title: t('common:ocr.upload.error.title'),
          message: e.message,
        });
        logError(e);
      })
      .finally(() => {
        setIsExtracting(false);
      });
  };

  const extractionErrorMessage = (() => {
    if (!extractionError?.message) {
      return undefined;
    }
    if (extractionError.message === FileInputErrorCodes.INVALID_FILE_TYPE) {
      return t('common:ocr.upload.error.invalidFileType');
    }
    if (extractionError.message === FileInputErrorCodes.TOO_MANY_FILES) {
      return t('common:ocr.upload.error.tooManyFiles');
    }
    return t('common:ocr.upload.error.extractionFailed');
  })();

  return (
    <div className="flex h-full flex-col gap-3">
      <FileInput
        isProcessing={isExtracting}
        loadingLabel={<Translate i18nKey="common:ocr.upload.loadingLabel" />}
        processingLabel={
          <Translate i18nKey="common:ocr.upload.processingLabel.main" values={{ dynamicLabel: processingLabel }} />
        }
        accept={{
          'application/pdf': ['.pdf'],
        }}
        hideSelectedFile
        onError={onError}
        onLoad={onLoadFile}
        loader={loader}
        dropzoneClassname="h-full"
        bottomSlot={
          isExtracting && unattendedOcrEmail ? <UnattendedOCRNudge unattendedOcrEmail={unattendedOcrEmail} /> : null
        }
      />
      {extractionError ? (
        <Alert severity="error" fullWidth withoutIcon className="rounded-md">
          <AlertSection severity="error" title={extractionError?.title ?? ''}>
            {extractionErrorMessage}
          </AlertSection>
        </Alert>
      ) : null}
    </div>
  );
};

const UnattendedOCRNudge = ({ unattendedOcrEmail }: { unattendedOcrEmail: string }) => {
  const { t } = useTranslation();
  const { isCopied, copyToClipboard } = useCopyToClipboard();

  return (
    <div className="flex flex-col gap-4 text-center text-sm text-gray-500">
      <p>{t('common:ocr.upload.unattendedNudge.title')}</p>
      <div className="flex items-center justify-between gap-2 rounded-3xl bg-white px-6 py-2">
        <span>{unattendedOcrEmail}</span>
        <Button
          size="extra-small"
          variant="minimal"
          onClick={() => copyToClipboard(unattendedOcrEmail)}
          analyticsId="ocr:upload-copy-unattended-email"
        >
          {isCopied
            ? t('common:ocr.upload.unattendedNudge.emailCopied')
            : t('common:ocr.upload.unattendedNudge.copyEmailAction')}
        </Button>
      </div>
    </div>
  );
};
